// paperCapital.js — Paper 전략 가상 자본 계산 헬퍼.
//
// paper-trader 백엔드는 KPI/포지션/거래 등 동적 데이터만 응답하고 "자본" 필드는
// 주지 않는다. 다만 모든 전략은 동일한 규칙으로 운영된다 — strategyMeta 의 각
// 설명에 일관되게 적힌 "자본 1천만 / 거래당 100만 KRW". 따라서 시작 자본은
// 프런트 상수로 고정하고, 현재 남은 자본은 시작 자본 + 실현 PnL(KRW) 로 계산한다.
//
// 만약 특정 전략이 다른 시작 자본으로 운영되면 strategyMeta 에 initialCapital 을
// 넣고 computeCapital(pnl, meta.initialCapital) 로 덮어쓸 수 있다 (현재 전부 균일).

/** 전략 1종의 기본 시작 자본 (KRW). strategyMeta 설명의 "자본 1천만" 과 일치. */
export const PAPER_INITIAL_CAPITAL_KRW = 10_000_000;

/** 자본 바 시각화의 축 상한 — 시작 자본의 배수. 중앙(=시작) 기준 좌우 대칭. */
const BAR_SCALE = 2;

/**
 * 실현 PnL(KRW) 로부터 자본 현황을 계산한다.
 *
 * @param {number} pnlKrw 실현 손익 (KRW). 비유한값은 0 으로 처리.
 * @param {number} [initial=PAPER_INITIAL_CAPITAL_KRW] 시작 자본 (그룹이면 N종 합산).
 * @returns {{initial:number, current:number, pnl:number, returnPct:number, fillPct:number}}
 *   - current   : 현재 남은 자본 = initial + pnl
 *   - returnPct : 수익률 (%) = pnl / initial * 100
 *   - fillPct   : 자본 바 채움 비율 (%) — 0~(BAR_SCALE*100), 50%·100%·…에 매핑.
 *                 트랙은 0~(BAR_SCALE×시작) 범위라 중앙(=BAR_SCALE*50%)이 시작 자본.
 */
export function computeCapital(pnlKrw, initial = PAPER_INITIAL_CAPITAL_KRW) {
    const base = Number.isFinite(initial) && initial > 0 ? initial : PAPER_INITIAL_CAPITAL_KRW;
    const pnl = Number.isFinite(pnlKrw) ? pnlKrw : 0;
    const current = base + pnl;
    const returnPct = (pnl / base) * 100;
    const rawFill = (current / base) * (100 / BAR_SCALE); // current==base → 50%
    const fillPct = Math.max(0, Math.min(100, rawFill));
    return { initial: base, current, pnl, returnPct, fillPct };
}

/** 자본 바에서 "시작 자본" 기준선이 위치할 트랙 비율 (%) — 항상 중앙. */
export const CAPITAL_BASELINE_PCT = 100 / BAR_SCALE;
